import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Camera as CameraIcon, Image as ImageIcon } from "lucide-react";

/**
 * CameraCaptureSheet — full-screen in-app camera used by the invoice scan page
 * to bypass unreliable <input type="file" capture="environment"> behaviour on
 * Android (some devices route to Google Photos even with the hint set).
 *
 * Props:
 *   open              : boolean — mount when true, unmount to release the camera
 *   onCapture(dataUrl): called with a JPEG data URL (max 1600px longest side, q=0.75)
 *   onCancel          : called when the user aborts (X or "Annuler")
 *   onFallbackGallery : called when the user opts to pick from the gallery instead
 *
 * Camera lifecycle:
 *   - stream is started on mount and stopped on unmount (all tracks .stop())
 *   - stream is also stopped after capture / on any error / on visibilitychange
 */
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.75;

export default function CameraCaptureSheet({ open, onCapture, onCancel, onFallbackGallery }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      try { s.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    }
    streamRef.current = null;
    setReady(false);
  }, []);

  // Start the camera when the sheet opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      setError(null);
      setReady(false);
      if (!navigator.mediaDevices?.getUserMedia) {
        setError({ code: "unsupported", msg: "Votre navigateur ne permet pas l'accès à la caméra." });
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          v.setAttribute("playsinline", "");
          v.muted = true;
          try { await v.play(); } catch (_) { /* autoplay blocked, ignored — the video will play once visible */ }
          setReady(true);
        }
      } catch (e) {
        let msg = "Impossible d'accéder à la caméra.";
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") msg = "Autorisation caméra refusée. Activez-la dans les paramètres du navigateur.";
        else if (name === "NotFoundError" || name === "OverconstrainedError") msg = "Aucune caméra disponible sur cet appareil.";
        else if (name === "NotReadableError") msg = "La caméra est déjà utilisée par une autre application.";
        setError({ code: name || "unknown", msg });
      }
    };
    start();
    return () => { cancelled = true; stopStream(); };
  }, [open, stopStream]);

  // Also stop the stream if the page/tab is hidden (safety net).
  useEffect(() => {
    if (!open) return;
    const onVisibility = () => { if (document.hidden) stopStream(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [open, stopStream]);

  const doCancel = () => { stopStream(); onCancel?.(); };
  const doFallback = () => { stopStream(); onFallbackGallery?.(); };

  const doCapture = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const vw = v.videoWidth || 0;
    const vh = v.videoHeight || 0;
    if (!vw || !vh) return;
    // Downsize to MAX_SIDE on the longest edge, matching processImageFile()'s pipeline.
    let dw = vw, dh = vh;
    if (Math.max(dw, dh) > MAX_SIDE) {
      const scale = MAX_SIDE / Math.max(dw, dh);
      dw = Math.round(dw * scale);
      dh = Math.round(dh * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, dw, dh);
    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    stopStream();
    onCapture?.(dataUrl);
  };

  if (!open) return null;

  return (
    <div
      data-testid="camera-sheet"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "#000", display: "flex", flexDirection: "column",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", color: "#fff", gap: 12 }}>
        <button
          onClick={doCancel}
          data-testid="camera-cancel"
          aria-label="Annuler"
          style={{ width: 40, height: 40, borderRadius: 20, background: "rgba(255,255,255,0.12)", border: 0, color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          <X size={20} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>Prendre une photo</div>
      </div>

      {/* Video / error surface */}
      <div style={{ flex: 1, position: "relative", background: "#000", overflow: "hidden" }}>
        {!error && (
          <video
            ref={videoRef}
            data-testid="camera-video"
            playsInline
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }}
          />
        )}
        {!error && !ready && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
            Démarrage de la caméra…
          </div>
        )}
        {error && (
          <div
            data-testid="camera-error"
            style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, color: "#fff", textAlign: "center", gap: 16 }}
          >
            <CameraIcon size={40} color="#f87171" />
            <div style={{ fontSize: 16, fontWeight: 700, maxWidth: 320 }}>{error.msg}</div>
            <button
              onClick={doFallback}
              data-testid="camera-fallback-gallery"
              style={{ marginTop: 8, padding: "12px 20px", borderRadius: 12, background: "#fff", color: "#000", border: 0, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <ImageIcon size={18} /> Choisir dans la galerie
            </button>
            <button
              onClick={doCancel}
              style={{ padding: "10px 16px", borderRadius: 12, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", cursor: "pointer", fontWeight: 600 }}
            >
              Fermer
            </button>
          </div>
        )}
      </div>

      {/* Footer with capture button — hidden if error */}
      {!error && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px 32px", background: "rgba(0,0,0,0.6)" }}>
          <button
            onClick={doCapture}
            disabled={!ready}
            data-testid="camera-shoot"
            aria-label="Prendre la photo"
            style={{
              width: 72, height: 72, borderRadius: 36,
              background: ready ? "#fff" : "rgba(255,255,255,0.4)",
              border: "4px solid rgba(255,255,255,0.7)",
              cursor: ready ? "pointer" : "not-allowed",
              boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
            }}
          />
        </div>
      )}
    </div>
  );
}
